import { createFileRoute } from "@tanstack/react-router";
import { SiteHeader } from "@/components/site/SiteHeader";
import { SiteFooter } from "@/components/site/SiteFooter";
import { SITE } from "@/lib/site";

const URL = `${SITE.url}/ethics`;
const LAST_UPDATED = "August 2026";

export const Route = createFileRoute("/ethics")({
  head: () => ({
    meta: [
      { title: `Ethics policy — ${SITE.name}` },
      { name: "description", content: "Map Observer's ethics policy: conflicts of interest, source confidentiality, and editorial integrity in covering local search and Google Business Profile." },
      { property: "og:title", content: "Ethics policy" },
      { property: "og:description", content: "Conflicts of interest, source protection, and editorial integrity." },
      { property: "og:url", content: URL },
      { property: "og:type", content: "article" },
    ],
    links: [{ rel: "canonical", href: URL }],
  }),
  component: Page,
});

function Page() {
  return (
    <div className="min-h-screen bg-background">
      <SiteHeader />
      <main className="mx-auto max-w-3xl px-5 sm:px-8 py-12">
        <p className="eyebrow">Policy</p>
        <h1 className="headline-display text-4xl mt-3">Ethics policy</h1>
        <p className="mt-4 text-sm text-muted-foreground">Last updated: {LAST_UPDATED}</p>

        <section className="mt-10 space-y-4 text-sm leading-relaxed">
          <h2 className="headline-display text-2xl">Conflicts of interest</h2>
          <p>Our largest and most structural conflict is disclosed up front: Map Observer is founded and funded by <a href="https://gbply.net" rel="sponsored noopener" className="text-ochre-deep underline underline-offset-4 hover:text-accent">GBPly</a>, a Google Business Profile management platform. We manage that conflict by keeping editorial control in the newsroom, disclosing the relationship on every page, and flagging it inline in any story that mentions GBPly or a direct competitor. The arrangement is set out in full on our <a href="/ownership-and-funding" className="text-accent underline">ownership and funding</a> page.</p>
          <p>Editors and contributors disclose any material relationship they have with companies, agencies, or individuals covered in their stories. A relationship that could reasonably be perceived as a conflict (consulting work, paid speaking, equity ownership, family ties) means that contributor does not write the story.</p>


          <h2 className="headline-display text-2xl mt-6">Source confidentiality</h2>
          <p>We honour off-the-record and background conversations. When a source asks for anonymity to discuss sensitive topics — Google policy enforcement, suspension waves, internal product changes — we evaluate the request on the merits and explain in the article why anonymity was granted when we publish.</p>

          <h2 className="headline-display text-2xl mt-6">Plagiarism and attribution</h2>
          <p>Original reporting and analysis from other publications is cited and linked. We do not republish other outlets&rsquo; work without attribution. Quotes are used in their original context.</p>

          <h2 className="headline-display text-2xl mt-6">User privacy</h2>
          <p>We do not sell reader data. Newsletter subscribers receive only the editions they opted into and can unsubscribe at any time. We use privacy-respecting analytics to understand which stories matter to our audience.</p>

          <h2 className="headline-display text-2xl mt-6">AI ethics</h2>
          <p>Where AI is used in our editorial process, we are transparent about it (see our <a href="/editorial-standards" className="text-accent underline">editorial standards</a>). We do not fabricate quotes, sources, or screenshots. We do not generate images of real people or businesses in ways that could be mistaken for genuine photography.</p>
        </section>
      </main>
      <SiteFooter />
    </div>
  );
}
